import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import * as Styled from "./styled";
import Cross from "@assets/icons/cross-thin.svg?react";
import ChevronRight from "@assets/icons/chevron-right.svg?react";
import ArrowLeft from "@assets/icons/arrow-left.svg?react";
import Button from "@/components/shared-ui/Button";
import { useEffect, useState } from "react";
import { type NavMenu } from "../../shared/nav.constants";
import useHeaderNavigation from "@/hooks/useHeaderNavigation";
import { toCamelCase } from "@/utils/helpers.utils";

type Props = {
  openNav: boolean;
  closeNav: () => void;
  openEditModal: () => void;
  isAdmin?: boolean;
};

const ExpandedMobileNav = ({
  openNav,
  closeNav,
  openEditModal,
  isAdmin,
}: Props) => {
  const { user, isAuthenticated, logout } = useAuth0();
  const headerNav = useHeaderNavigation();

  const isAdminUser =
    user && user["https://customer.seccl.tech/roles"].includes("admin");

  const [menu, setMenu] = useState<NavMenu[]>([]);
  const [secondaryMenu, setSecondaryMenu] = useState<NavMenu>();

  const resetMenus = () => {
    if (headerNav) {
      setMenu(headerNav);
      setSecondaryMenu(undefined);
    }
  };

  useEffect(() => {
    if (headerNav) {
      setMenu(headerNav);
    }
  }, [headerNav]);
  return (
    <>
      <Styled.HamburgerMenu data-testid="hamburgerMenu" />
      {isAuthenticated && user && (
        <Styled.Dropdown
          open={openNav}
          secondaryMenu={secondaryMenu !== undefined}
          data-testid="mobileNavDropdown"
        >
          <Styled.NavActions>
            {secondaryMenu && !isAdmin && (
              <Styled.BackButton
                onClick={(e) => {
                  e.stopPropagation();
                  resetMenus();
                }}
                data-testid="mobileNavBackButton"
              >
                <ArrowLeft /> Back
              </Styled.BackButton>
            )}
            <Styled.CloseSection>
              <Cross
                onClick={(e) => {
                  e.stopPropagation();
                  resetMenus();
                  closeNav();
                }}
                data-testid="closeMobileNavButton"
              />
            </Styled.CloseSection>
          </Styled.NavActions>
          {!isAdmin && (
            <>
              {secondaryMenu && (
                <Styled.DropdownSection $withBackground>
                  <Styled.DropdownSubMenuHeading>
                    <span>{secondaryMenu.title}</span>
                    {secondaryMenu.description}
                  </Styled.DropdownSubMenuHeading>
                </Styled.DropdownSection>
              )}
              {menu.map((item) => {
                if (item.secondary) {
                  const camelCaseTitle = toCamelCase(item.title);
                  return (
                    <Styled.DropdownItem
                      key={item.title}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.secondary) {
                          setMenu(item.secondary);
                        }
                        setSecondaryMenu(item);
                      }}
                      submenu
                      data-testid={`${camelCaseTitle}NavDropdown`}
                    >
                      {item.title} <ChevronRight />
                    </Styled.DropdownItem>
                  );
                } else if (item.to || item.disabled) {
                  return (
                    <Styled.DropdownItem
                      key={item.title}
                      as={Link}
                      to={item.to || ""}
                      onClick={resetMenus}
                      data-testid={`${toCamelCase(item.title)}NavItem`}
                      $disabled={item.disabled}
                    >
                      {item.icon && <item.icon />}
                      {item.title}
                      {item.external && <Styled.ExternalLink />}
                    </Styled.DropdownItem>
                  );
                }
                return null;
              })}
            </>
          )}
          {(!secondaryMenu || isAdmin) && (
            <>
              <Styled.DropdownSection $withBackground>
                <Styled.DropdownHeading>Logged in as:</Styled.DropdownHeading>
                <strong>{user.name}</strong>
                <Styled.DropdownLightText>
                  {user.email}
                </Styled.DropdownLightText>
                <Button
                  label="Edit profile"
                  type="button"
                  variant="tertiary"
                  onClick={openEditModal}
                />
              </Styled.DropdownSection>
              {isAdminUser && (
                <Styled.DropdownItem
                  as={Link}
                  to={isAdmin ? "/" : "/admin"}
                  data-testid="viewAdminPortalButton"
                >
                  {isAdmin ? "View customer hub" : "View admin portal"}
                </Styled.DropdownItem>
              )}
              <Styled.LogoutSection>
                <Button label="Contact us" internalLink="/contact" />
                <Button
                  onClick={() =>
                    logout({
                      logoutParams: { returnTo: window.location.origin },
                    })
                  }
                  label="Logout"
                  noArrow
                  inverse
                />
              </Styled.LogoutSection>
            </>
          )}
        </Styled.Dropdown>
      )}
    </>
  );
};

export default ExpandedMobileNav;
